import React, { useState, useEffect } from 'react';
import { useData } from '../../context/DataContext';
import { cn } from '../../lib/utils';
import { Filter, Calendar, Clock, Coffee, Monitor, Users } from 'lucide-react';
import MappingSelectorModal from './MappingSelectorModal';

export default function Timetable() {
    const {
        mappings, subjects, sections, faculty, departments, timeConfigs,
        timetableEntries, updateTimetableEntry, fetchTimetableForSection
    } = useData();

    // Filters
    const [selectedDept, setSelectedDept] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedSection, setSelectedSection] = useState('');

    // State for dynamic slots
    const [slots, setSlots] = useState([]);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [targetSlot, setTargetSlot] = useState(null); // { day, slotId }

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

    // Auto-select section filters if possible or reset
    useEffect(() => {
        if (selectedSection) {
            fetchTimetableForSection(selectedSection);

            // Find config for this section to render grid
            const section = sections.find(s => s.id == selectedSection);
            if (section && section.timeConfigId) {
                fetch(`/api/time-configs/${section.timeConfigId}/slots`)
                    .then(res => res.json())
                    .then(data => setSlots(data));
            }
        } else {
            setSlots([]); // Clear grid if no section selected
        }
    }, [selectedSection, sections]);


    const getMappingDetails = (mappingId) => {
        const mapping = mappings.find(m => m.id === mappingId);
        if (!mapping) return null;
        const subj = subjects.find(s => s.id === mapping.subjectId);
        const fac = faculty.find(f => f.id === mapping.facultyId);
        return {
            subject: subj?.name,
            code: subj?.code,
            faculty: fac?.name,
            email: fac?.email
        };
    };

    // Filter available sections based on Dept & Year
    const availableSections = sections.filter(sec => {
        let match = true;
        if (selectedDept) match = match && sec.departmentId == selectedDept;
        if (selectedYear) match = match && sec.year == selectedYear;
        return match;
    });

    // Helper to get enriched mappings for the modal
    const getEnrichedSectionMappings = () => {
        if (!selectedSection) return [];
        return mappings
            .filter(m => m.sectionId == selectedSection)
            .map(m => {
                const subj = subjects.find(s => s.id === m.subjectId);
                const fac = faculty.find(f => f.id === m.facultyId);
                return {
                    ...m,
                    subjectName: subj ? `${subj.name} (${subj.code})` : (m.subjectName || 'Unknown Subject'),
                    facultyName: fac ? fac.name : (m.facultyName || 'Unknown Faculty')
                };
            });
    };

    const handleSlotClick = async (day, slot) => {
        if (!selectedSection) return;
        if (slot.type === 'BREAK') return; // Cannot schedule during break

        const key = `${day}-${slot.id}`;
        const mappingId = timetableEntries[key];

        if (mappingId) {
            if (window.confirm('Clear this slot?')) {
                await updateTimetableEntry(day, slot.id, selectedSection, null);
            }
        } else {
            // Open Modal to select mapping
            setTargetSlot({ day, slotId: slot.id });
            setIsModalOpen(true);
        }
    };

    const handleMappingSelect = async (mappingWithDuration) => {
        if (targetSlot && selectedSection) {
            const { duration, faculty2Id, ...mapping } = mappingWithDuration; // Duration & Fac2 passed from modal
            const loops = duration || 1;

            // Find index of target slot
            const startIndex = slots.findIndex(s => s.id === targetSlot.slotId);

            if (startIndex === -1) return;

            // Apply for duration
            for (let i = 0; i < loops; i++) {
                const currentSlot = slots[startIndex + i];
                if (!currentSlot) break; // End of day
                if (currentSlot.type === 'BREAK') break; // Stop at break

                await updateTimetableEntry(targetSlot.day, currentSlot.id, selectedSection, mapping.id, faculty2Id);
            }

            setIsModalOpen(false);
            setTargetSlot(null);
        }
    };

    return (
        <div className="space-y-6">
            <MappingSelectorModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSelect={handleMappingSelect}
                mappings={getEnrichedSectionMappings()}
                facultyList={faculty}
            />

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-primary">Timetable Management</h1>
                    <p className="text-muted-foreground">Configure schedules for Departments & Years</p>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2 bg-card p-2 rounded-xl border border-border shadow-sm">
                    <div className="relative">
                        <select
                            className="bg-secondary/50 pl-8 pr-4 py-2 rounded-lg text-sm border-none focus:ring-1 focus:ring-primary appearance-none"
                            value={selectedDept}
                            onChange={(e) => setSelectedDept(e.target.value)}
                        >
                            <option value="">All Departments</option>
                            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                        <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    </div>

                    <select
                        className="bg-secondary/50 px-4 py-2 rounded-lg text-sm border-none focus:ring-1 focus:ring-primary appearance-none"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                    >
                        <option value="">All Years</option>
                        <option value="1">1st Year</option>
                        <option value="2">2nd Year</option>
                        <option value="3">3rd Year</option>
                        <option value="4">4th Year</option>
                    </select>

                    <select
                        className="bg-primary/10 text-primary font-medium px-4 py-2 rounded-lg text-sm border-none focus:ring-1 focus:ring-primary appearance-none"
                        value={selectedSection}
                        onChange={(e) => setSelectedSection(e.target.value)}
                    >
                        <option value="">Select Section</option>
                        {availableSections.map(s => <option key={s.id} value={s.id}>{s.name} (Year {s.year})</option>)}
                    </select>
                </div>
            </div>

            {selectedSection ? (
                <div className="overflow-x-auto pb-4">
                    <div className="min-w-[1000px] bg-card border border-border rounded-xl overflow-hidden shadow-sm">
                        {/* Header Row */}
                        <div className="flex bg-secondary text-sm font-semibold text-muted-foreground divide-x divide-border">
                            <div className="w-24 p-4 flex-shrink-0 flex items-center justify-center bg-muted/50 sticky left-0">Day</div>
                            {slots.map(slot => (
                                <div
                                    key={slot.id}
                                    className={cn(
                                        "p-3 text-center flex-1 flex flex-col justify-center min-w-[120px]",
                                        slot.type === 'LAB' && "min-w-[200px] bg-blue-50/50 dark:bg-blue-900/10",
                                        slot.type === 'BREAK' && "min-w-[60px] bg-orange-50/50 dark:bg-orange-900/10"
                                    )}
                                >
                                    <span className="block text-foreground">{slot.name}</span>
                                    <span className="text-[10px] opacity-70 flex items-center justify-center gap-1">
                                        <Clock size={10} /> {slot.startTime} - {slot.endTime}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Rows */}
                        <div className="divide-y divide-border">
                            {days.map(day => (
                                <div key={day} className="flex divide-x divide-border hover:bg-muted/5 transition-colors">
                                    <div className="w-24 p-4 font-medium text-sm flex items-center justify-center bg-secondary/30 flex-shrink-0 sticky left-0">{day}</div>
                                    {(() => {
                                        // Logic to merge cells
                                        const renderedCells = [];
                                        let skipCount = 0;

                                        slots.forEach((slot, index) => {
                                            if (skipCount > 0) {
                                                skipCount--;
                                                return;
                                            }

                                            if (slot.type === 'BREAK') {
                                                renderedCells.push(
                                                    <div key={`${day}-${slot.id}`} className="min-w-[60px] bg-orange-50/30 dark:bg-orange-900/5 flex items-center justify-center">
                                                        <Coffee size={16} className="text-orange-400 opacity-50" />
                                                    </div>
                                                );
                                                return;
                                            }

                                            // Check current mapping
                                            const key = `${day}-${slot.id}`;
                                            const entry = timetableEntries[key]; // Now an object or undefined
                                            const mappingId = entry ? entry.mappingId : null;
                                            const faculty2Id = entry ? entry.faculty2Id : null;

                                            let colSpan = 1;

                                            // Check next slots for same mapping to merge
                                            if (mappingId) {
                                                for (let i = index + 1; i < slots.length; i++) {
                                                    const nextSlot = slots[i];
                                                    if (nextSlot.type === 'BREAK') break;

                                                    const nextKey = `${day}-${nextSlot.id}`;
                                                    const nextEntry = timetableEntries[nextKey];
                                                    // Merge if mappingId is same
                                                    if (nextEntry && nextEntry.mappingId === mappingId) {
                                                        colSpan++;
                                                    } else {
                                                        break;
                                                    }
                                                }
                                            }

                                            // Apply skip
                                            skipCount = colSpan - 1;

                                            const details = mappingId ? getMappingDetails(mappingId) : null;
                                            const fac2 = faculty2Id ? faculty.find(f => f.id == faculty2Id) : null;

                                            renderedCells.push(
                                                <div
                                                    key={key}
                                                    onClick={() => handleSlotClick(day, slot)}
                                                    className={cn(
                                                        "p-2 min-h-[100px] cursor-pointer transition-all hover:bg-primary/5 flex flex-col justify-center items-center text-center gap-1 group relative border-r border-border last:border-r-0",
                                                        slot.type === 'LAB' && "min-w-[200px]",
                                                        !slot.type !== 'LAB' && "min-w-[120px]",
                                                        details ? "bg-accent/10" : "bg-transparent"
                                                    )}
                                                    style={{ flex: colSpan, width: `${colSpan * 100}%` }}
                                                >
                                                    {details ? (
                                                        <>
                                                            <span className="text-xs font-bold text-primary">{details.code}</span>
                                                            <span className="text-xs font-medium text-foreground line-clamp-2">{details.subject}</span>
                                                            <div className="flex flex-col items-center gap-0.5 mt-1">
                                                                <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-background/50 px-2 py-0.5 rounded-full border border-border/50">
                                                                    <Monitor size={10} />
                                                                    {details.faculty}
                                                                </div>
                                                                {fac2 && (
                                                                    <div className="flex items-center gap-1 text-[10px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-100">
                                                                        <Users size={10} />
                                                                        {fac2.name}
                                                                    </div>
                                                                )}
                                                                {details.email && (
                                                                    <span className="text-[10px] text-muted-foreground/70">{details.email}</span>
                                                                )}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <span className="text-xs text-muted-foreground/50 font-medium border border-dashed border-border px-2 py-1 rounded">+ Assign</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });

                                        return renderedCells;
                                    })()}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 bg-card border border-dashed border-border rounded-3xl text-center">
                    <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mb-4 text-primary">
                        <Calendar size={32} />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Select a Section</h3>
                    <p className="text-muted-foreground max-w-sm">
                        Please select a Department, Year, and Section from the filters above to view and configure the timetable.
                    </p>
                </div>
            )}
        </div>
    );
}
